import React from "react";
import TrainingList from "./TrainingList";
import TrainingDetail from "./TrainingDetail";
import TrainingProgress from "./TrainingProgress";

/**
 * TrainingDesktop - Desktop-specific shell for the Training feature.
 *
 * Renders the desktop-optimised header (horizontal search + tab row) and
 * delegates list / progress / detail content to the shared child components.
 *
 * All state and event handlers are owned by the parent Training.js and passed
 * in as props so this component stays a pure presentation layer.
 */
const TrainingDesktop = ({
  currentView,
  selectedTraining,
  selectedSession,
  searchExpanded,
  searchQuery,
  activeSearchTerm,
  searchInputRef,
  searchContainerRef,
  setCurrentView,
  setSearchQuery,
  setSearchExpanded,
  handleSelectTraining,
  handleBackToList,
  handleSearch,
  handleSearchKeyPress,
  clearSearch,
  handleTrainingComplete,
}) => {
  // Detail view has its own back button; render it directly without the page header.
  if (currentView === "detail" && selectedTraining) {
    return (
      <TrainingDetail
        trainingUuid={selectedTraining.uuid}
        sessionId={selectedSession}
        onBack={handleBackToList}
        onComplete={handleTrainingComplete}
        isMobile={false}
      />
    );
  }

  return (
    <div className="common-container training-container">
      {/* ── Desktop Header ── */}
      <div className="common-header">
        <div className="training-header-wrapper">
          <div className="training-header-content">
            <div className="training-title-section">
              <h1 className="main-title">Training Center</h1>
              <p className="common-subtitle">
                Browse security trainings and track your progress
              </p>
            </div>

            <div className="training-header-actions">
              {/* Search icon – expands the search input */}
              {currentView === "list" &&
                !searchExpanded &&
                !activeSearchTerm && (
                  <button
                    className="training-search-icon-btn"
                    onClick={() => setSearchExpanded(true)}
                    title="Search trainings"
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="11" cy="11" r="8" />
                      <path d="m21 21-4.35-4.35" />
                    </svg>
                  </button>
                )}

              {/* Expanded search bar */}
              {currentView === "list" &&
                (searchExpanded || activeSearchTerm) && (
                  <div
                    className="training-search-container"
                    ref={searchContainerRef}
                  >
                    <div className="training-search-row">
                      <div className="training-search-input-wrapper">
                        <input
                          ref={searchInputRef}
                          type="text"
                          placeholder="Search trainings..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          onKeyPress={handleSearchKeyPress}
                          className="training-search-input"
                        />
                        {searchQuery && (
                          <button
                            onClick={() => setSearchQuery("")}
                            className="training-search-clear-btn"
                            title="Clear input"
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M18 6L6 18" />
                              <path d="M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                      <button
                        className="btn btn-primary training-search-btn training-search-submit-btn"
                        onClick={handleSearch}
                      >
                        Search
                      </button>
                    </div>

                    {activeSearchTerm && (
                      <div className="training-search-criteria">
                        <span className="training-search-criteria-label">
                          Search:
                        </span>
                        <span className="training-search-criteria-term">
                          {activeSearchTerm}
                        </span>
                        <button
                          className="training-search-criteria-clear"
                          onClick={clearSearch}
                          title="Clear search criteria"
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M18 6L6 18" />
                            <path d="M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                )}

              {/* Browse / My Progress tabs */}
              <div className="training-nav-tabs">
                <button
                  className={`training-nav-tab ${currentView === "list" ? "active" : ""}`}
                  onClick={() => setCurrentView("list")}
                >
                  Browse
                </button>
                <button
                  className={`training-nav-tab ${currentView === "progress" ? "active" : ""}`}
                  onClick={() => setCurrentView("progress")}
                >
                  My Progress
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Content Area ── */}
      <div className="training-page-content">
        {currentView === "list" && (
          <TrainingList
            onSelectTraining={handleSelectTraining}
            searchQuery={searchQuery}
          />
        )}
        {currentView === "progress" && (
          <TrainingProgress onSelectTraining={handleSelectTraining} />
        )}
      </div>
    </div>
  );
};

export default TrainingDesktop;
